import { Switch } from '@mantine/core';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { NotificationPreferenceModel } from '@xver/apis/v1/notification-preferences/model';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';

interface NotificationsSectionProps {
    preferences: NotificationPreferenceModel;
    onToggle: (
        key: 'emailNotifications' | 'smsNotifications' | 'marketingOptIn',
        next: boolean
    ) => void;
    isSaving?: boolean;
}

export default function NotificationsSection({
    preferences,
    onToggle,
    isSaving,
}: NotificationsSectionProps) {
    const { t } = useTranslation('settings');

    return (
        <DialogBox
            label={t('notifications.title')}
            description={t('notifications.description')}
            classNames="p-4 gap-4"
        >
            <ToggleRow
                title={t('notifications.email')}
                description={t('notifications.emailDescription')}
                checked={preferences.emailNotifications}
                onChange={(next) => onToggle('emailNotifications', next)}
                isSaving={isSaving}
            />
            <ToggleRow
                title={t('notifications.sms')}
                description={t('notifications.smsDescription')}
                checked={preferences.smsNotifications}
                onChange={(next) => onToggle('smsNotifications', next)}
                isSaving={isSaving}
            />
            <ToggleRow
                title={t('notifications.marketing')}
                description={t('notifications.marketingDescription')}
                checked={preferences.marketingOptIn}
                onChange={(next) => onToggle('marketingOptIn', next)}
                isSaving={isSaving}
            />
        </DialogBox>
    );
}

interface ToggleRowProps {
    title: string;
    description: string;
    checked: boolean;
    onChange: (next: boolean) => void;
    isSaving?: boolean;
}

function ToggleRow({ title, description, checked, onChange, isSaving }: ToggleRowProps) {
    const titleId = useId();
    const descriptionId = useId();
    return (
        <div className="w-full flex flex-row justify-between items-start gap-4">
            <div className="flex flex-col gap-1 max-w-prose">
                <span id={titleId} className="text-sm font-medium">
                    {title}
                </span>
                <span id={descriptionId} className="text-xs text-fg/68">
                    {description}
                </span>
            </div>
            <Switch
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                checked={checked}
                onChange={(e) => !isSaving && onChange(e.currentTarget.checked)}
            />
        </div>
    );
}
